import React, { useState, useEffect, createContext } from 'react';
import {
    Box,
    CssBaseline,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardSharp from '@mui/icons-material/DashboardSharp';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, Outlet } from 'react-router-dom';
import {
    BedroomParent,
    FaceRetouchingNatural,
} from "@mui/icons-material";
import logo from "../assests/img/chik-logo.png";

const drawerWidth = 240;

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        }),
    }),
);

// Create Cart Context
export const CartContext = createContext();

export default function PersistentDrawerLeft() {
    const theme = useTheme();
    const [open, setOpen] = useState(true);
    const [selected, setSelected] = useState('/home');
    const navigate = useNavigate();
    const [currentTime, setCurrentTime] = useState(new Date());

    // Cart state
    const [cartItems, setCartItems] = useState([]);

    const role = localStorage.getItem('role');
    const isAdmin = role === 'admin';

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        // Load cart from localStorage on mount
        const savedCart = localStorage.getItem('cartItems');
        if (savedCart) {
            try {
                setCartItems(JSON.parse(savedCart));
            } catch (error) {
                console.error("Invalid cart data in localStorage", error);
            }
        }
    }, []);

    useEffect(() => {
        // Save cart to localStorage on change
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
    }, [cartItems]);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);

    const handleNavigation = (path) => {
        setSelected(path);
        navigate(path);
        handleDrawerClose();
    };

    const handleLogout = () => {
        localStorage.removeItem('role');
        localStorage.removeItem('cartItems');
        navigate('/', { replace: true });
    };

    const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

    const formattedDate = currentTime.toLocaleDateString();
    const formattedTime = currentTime.toLocaleTimeString();
    console.log(cartCount);
    return (
        <CartContext.Provider value={{ cartItems, setCartItems }}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar position="fixed" open={open} sx={{ backgroundColor: 'white' }}>
                    <Toolbar sx={{ justifyContent: 'center' }}>
                        <IconButton
                            color="error"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{ mr: 2, ...(open && { display: 'none' }) }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Typography
                            variant="h4"
                            noWrap
                            component="div"
                            className="text-black"
                            sx={{ flexGrow: 1, fontWeight: 'bold' }}
                        >
                            Chicago Restaurant
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                            <Box component="img" src={logo} alt="Logo" sx={{ height: 80, mx: 2 }} />
                        </Box>

                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                                {formattedDate} - {formattedTime}
                            </Typography>
                        </Box>

                    </Toolbar>

                </AppBar>

                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: 'none',
                            boxShadow: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    <DrawerHeader>
                        <IconButton onClick={handleDrawerClose}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>

                    {/* Optional: Role display at the top */}
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FaceRetouchingNatural sx={{ fontSize: 20, color: 'gray', mr: 1 }} />
                            Logged in as: <strong style={{ marginLeft: 6 }}>{role?.toUpperCase()}</strong>
                        </Typography>
                    </Box>

                    <List>
                        <ListItem disablePadding sx={{ mb: 3, ml: 2 }}>
                            <ListItemButton
                                onClick={() => handleNavigation('/home')}
                                sx={{
                                    borderRadius: 2,
                                    '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.2)' },
                                    backgroundColor: selected === '/home' ? 'rgba(255, 165, 0, 0.2)' : 'transparent',
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: '48px', color: '#ff9800' }}>
                                    <DashboardSharp sx={{ fontSize: 32 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Dashboard"
                                    primaryTypographyProps={{ color: 'text.primary' }}
                                />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding sx={{ mb: 3, ml: 2 }}>
                            <ListItemButton
                                onClick={() => handleNavigation('/report')}
                                sx={{
                                    borderRadius: 2,
                                    '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.2)' },
                                    backgroundColor: selected === '/report' ? 'rgba(255, 165, 0, 0.2)' : 'transparent',
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: '48px', color: '#ff9800' }}>
                                    <BedroomParent sx={{ fontSize: 32 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Report"
                                    primaryTypographyProps={{ color: 'text.primary' }}
                                />
                            </ListItemButton>
                        </ListItem>

                        {isAdmin && (
                            <>
                                <ListItem disablePadding sx={{ mb: 3, ml: 2 }}>
                                    <ListItemButton
                                        onClick={() => handleNavigation('/order')}
                                        sx={{
                                            borderRadius: 2,
                                            '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.2)' },
                                            backgroundColor: selected === '/order' ? 'rgba(255, 165, 0, 0.2)' : 'transparent',
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: '48px', color: '#ff9800' }}>
                                            <FaceRetouchingNatural sx={{ fontSize: 32 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Orders"
                                            primaryTypographyProps={{ color: 'text.primary' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            </>
                        )}
                    </List>

                    <Box sx={{ flexGrow: 1 }} /> {/* Push logout to bottom */}

                    {/* Divider and logout section */}
                    <Box sx={{ px: 2, mb: 2 }}>

                        <button
                            style={{
                                width: '100%',
                                padding: '10px 16px',
                                backgroundColor: 'orange',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                            }}
                            onClick={handleLogout}
                        >
                            Logout
                        </button>
                    </Box>
                </Drawer>


                <Main open={open}>
                    <DrawerHeader />
                    <Outlet />
                </Main>
            </Box>
        </CartContext.Provider>
    );
}
